import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'

export type IntegrationStatus = 'active' | 'inactive'
export type IntegrationDirection = 'inbound' | 'outbound' | 'bidirectional'
/** Which Epic system an LIS integration releases results to. */
export type ResultsDestination = 'beaker' | 'beacon'

export const RESULTS_DESTINATION_LABEL: Record<ResultsDestination, string> = {
  beaker: 'Beaker',
  beacon: 'Beacon',
}

export type IntegrationCategory =
  | 'instrument'
  | 'ehr'
  | 'lis'
  | 'printer'
  | 'immunoassay'
  | 'cell_counter'
  | 'other'
export type TransportProtocol = 'hl7' | 'astm' | 'fhir' | 'csv' | 'json' | 'api' | 's3'

export type Integration = {
  id: string
  tenantId: string
  sourceId: string
  name: string
  status: IntegrationStatus
  config: Record<string, unknown>
  health: Record<string, unknown>
  metadata: Record<string, unknown>
  hasCredentials: boolean
  category: IntegrationCategory
  protocol: TransportProtocol | null
  direction: IntegrationDirection
  viaMiddleware: boolean
  /** LIS only: the Epic system outgoing results are released to (z8nrz7ffzu). */
  resultsDestination: ResultsDestination
  createdBy: string
  createdAt: string
  updatedAt: string
}

export type CreateIntegrationPayload = {
  name: string
  sourceId: string
  config?: Record<string, unknown>
  /** Write-only credentials. Sent to the backend, stored in Secrets Manager. */
  secret?: Record<string, unknown>
  status?: IntegrationStatus
  category?: IntegrationCategory
  protocol?: TransportProtocol
  direction?: IntegrationDirection
  viaMiddleware?: boolean
  resultsDestination?: ResultsDestination
}

export type UpdateIntegrationPayload = Partial<Omit<CreateIntegrationPayload, 'sourceId'>>

export type TopologyNode = {
  id: string
  type: 'platform' | 'middleware' | 'integration'
  label: string
  sublabel?: string
  status?: IntegrationStatus
  category?: IntegrationCategory
  protocol?: TransportProtocol
  direction?: IntegrationDirection
  viaMiddleware?: boolean
}

export type TopologyEdge = {
  id: string
  source: string
  target: string
  label?: string
  direction?: IntegrationDirection
}

export type Topology = { nodes: TopologyNode[]; edges: TopologyEdge[] }

// ── Instrument data (CGT-P06 consumer) ──────────────────────────────────────
export type InstrumentResultType = 'ella' | 'nc200' | 'epic'
export type InstrumentResultStatus = 'completed' | 'quarantined' | 'correlated'

/** A persisted normalized result row (payload carries the variant-specific fields). */
export type InstrumentResultRow = {
  id: string
  integrationId: string | null
  din: string
  productCode: string
  resultType: InstrumentResultType
  rowKey: string
  externalId: string
  version: number
  supersedes: number | null
  status: InstrumentResultStatus
  flags: string[]
  reason: string | null
  equipmentExternalId: string | null
  equipmentModel: string | null
  configVersionAtIngest: string | null
  sourceObject: string
  ingestedAt: string
  payload: Record<string, unknown>
  isLatest: boolean
  correlationId: string | null
  procedureId: string | null
  createdAt: string
  updatedAt: string
}

export type IngestSummary = {
  din: string
  productCode: string
  correlationId: string | null
  procedureId: string | null
  received: number
  persisted: number
  quarantined: number
}

export type Equipment = {
  id: string
  type: string
  model: string
  manufacturer: string
  vendorNumber: string
  serialOrLot: string
  configVersion: string
  effectiveDate: string
}
export type EquipmentList = { equipment: Equipment[] }

export type Feed = {
  feedId: string
  equipmentId: string
  displayName: string
  transport: 'SMB' | 'SFTP' | 'S3' | 'API'
  connectionState: 'connected' | 'degraded' | 'down'
  lastSuccessfulExchange: string | null
  queueDepth: number
  errorCount: number
  quarantineCount: number
}
export type FeedList = { feeds: Feed[] }

/**
 * All integration requests are scoped to the selected tenant via the
 * `X-Tenant-Id` header.
 */
async function request<T>(path: string, tenantId: string, init?: RequestInit): Promise<T> {
  const res = await authFetch(`${BASE}${path}`, {
    headers: authHeaders({ 'X-Tenant-Id': tenantId, ...init?.headers }),
    ...init,
  })
  if (!res.ok) {
    handleUnauthorized(res.status)
    const text = await res.text().catch(() => res.statusText)
    throw new Error(`API ${init?.method ?? 'GET'} ${path} failed (${res.status}): ${text}`)
  }
  return res.json() as Promise<T>
}

export const integrationsApi = {
  list: (tenantId: string) => request<Integration[]>('/integrations', tenantId),

  get: (tenantId: string, id: string) => request<Integration>(`/integrations/${id}`, tenantId),

  create: (tenantId: string, payload: CreateIntegrationPayload) =>
    request<Integration>('/integrations', tenantId, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  update: (tenantId: string, id: string, payload: UpdateIntegrationPayload) =>
    request<Integration>(`/integrations/${id}`, tenantId, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  remove: (tenantId: string, id: string) =>
    request<Integration>(`/integrations/${id}`, tenantId, { method: 'DELETE' }),

  // ── Topology (diagram) ──
  getTopology: (tenantId: string) => request<Topology>('/integrations/topology', tenantId),

  // ── Instrument data: fetch from the integration-service + view ──
  fetchResults: (
    tenantId: string,
    id: string,
    body: { din: string; productCode: string; scenario?: string },
  ) =>
    request<IngestSummary>(`/integrations/${id}/fetch`, tenantId, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  listResults: (
    tenantId: string,
    id: string,
    params: { din?: string; productCode?: string; latestOnly?: boolean } = {},
  ) => {
    const q = new URLSearchParams()
    if (params.din) q.set('din', params.din)
    if (params.productCode) q.set('productCode', params.productCode)
    if (params.latestOnly) q.set('latestOnly', 'true')
    const qs = q.toString()
    return request<InstrumentResultRow[]>(
      `/integrations/${id}/results${qs ? `?${qs}` : ''}`,
      tenantId,
    )
  },

  getEquipment: (tenantId: string, id: string) =>
    request<EquipmentList>(`/integrations/${id}/equipment`, tenantId),

  getFeeds: (tenantId: string, id: string) =>
    request<FeedList>(`/integrations/${id}/feeds`, tenantId),
}
